import React, {createContext, useContext, useReducer, useMemo, useCallback} from 'react';
import {LoadingState, groupReducer} from '../helper/utils';
import {Chat, MessageGroup, Message, User} from "../models";
import {
    fetchChats,
    getMessages,
    sendMessage,
    readMessages,
    Read
} from '../services/chat';

type ChatMessages = {lastUnread?: string, groups: MessageGroup[]}
type  ChatState = {
    chats: Chat[]
    currentChatId?: string
    inputMessage: string
    loadingState: LoadingState
    message?: {desc: string, title: string}
    user?: User
}
type ChatAddition = {chatID: string, values: Message[]};
type MessageRead = {ids: string[], chatId: string, readerId: string}
type ChatAction =
| {type: "set_chats", payload: Chat[]}
| {type: "acknowledge", payload: MessageRead}
| {type: "add_chat", payload: Chat}
| {type: "set_user", payload: User}
| {type: "set_current_chat", payload: string | undefined}
| {type: "add_messages", payload: ChatAddition}
| {type: "set_error", payload?: {desc: string, title: string}}
| {type: "set_input_message", payload: string}
| {type: "set_loading_state", payload: LoadingState};

const initialState: ChatState = {
    chats: [],
    inputMessage: '',
    loadingState: LoadingState.None,
};

const ChatContext = createContext<ReturnType<typeof useChatValue> | undefined>(undefined);

function chatReducer(state: ChatState, action: ChatAction): ChatState {
    switch (action.type) {
        case "set_chats":
            return {
                ...state,
                chats: Chat.mergeChats(state.chats,action.payload)
            };
        case "add_chat":
            return {...state, chats: state.chats.concat([action.payload])}
        case "set_error": return {...state, message: action.payload};
        case "set_user":
            return {...state, user: action.payload};
        case "set_current_chat":{
            return {...state, currentChatId: action.payload};
        }
        case "add_messages":
            return {
                ...state,
                chats: state.chats.map(function (c) {
                    let res;
                    if (c.id === action.payload.chatID) {
                        res =  Chat.addMessages(c, action.payload.values);
                        return res;
                    }
                    return c;
                })
            };
        case "acknowledge": {
            const {chatId, ids, readerId} = action.payload
            return {
                ...state,
                chats: state.chats.map(function (c) {
                    let res;
                    if (c.id === chatId) {
                        res = Chat.acknowledgeMessages(c, readerId, ids);
                        return res;
                    }
                    return c;
                })
            };
        }
        case "set_input_message":
            return {...state, inputMessage: action.payload};
        case "set_loading_state":
            return {...state, loadingState: action.payload};
        default:
            return state;
    }
}

function createChatActions(dispatch: React.Dispatch<ChatAction>) {
    return {
        acknowledge: (payload: MessageRead) => dispatch(
            {type: "acknowledge", payload}
        ),
        addChat: (payload: Chat) => dispatch({type: "add_chat", payload}),
        clearError: () => dispatch({type: "set_error"}),
        setChats: (payload: Chat[]) => dispatch({type: "set_chats", payload}),
        setUser: (payload: User) => dispatch({type: "set_user", payload}),
        setCurrentChat: (payload?: string) => dispatch({type: "set_current_chat", payload}),
        addMessages: (payload: ChatAddition) => dispatch({type: "add_messages", payload}),
        setInputMessage: (payload: string) => dispatch({type: "set_input_message", payload}),
        setLoadingState: (payload: LoadingState) => dispatch({type: "set_loading_state", payload}),
    };
}

function useChatValue() {
    const [state, dispatch] = useReducer(chatReducer, initialState);
    const actions = useMemo(() => createChatActions(dispatch), [dispatch]);
    const getChat = useCallback(async function () {
        let chats;
        actions.setLoadingState(LoadingState.Pending);
        try {
            chats = await fetchChats("active");
            actions.setChats(chats);
            actions.setLoadingState(LoadingState.Success);
        } catch (error) {
            actions.setLoadingState(LoadingState.Error);
        }
    }, [actions]);
    const fetchMessages = useCallback(async function (chatID: string) {
        let res;
        actions.setLoadingState(LoadingState.Pending);
        res = await getMessages(chatID);
        if (res.type === "success") {
            actions.addMessages({chatID, values: res.value});
            actions.setLoadingState(LoadingState.Success);
        } else {
            actions.setLoadingState(LoadingState.Error);
            dispatch({type: "set_error", payload: {
                title: "Error fetching message",
                desc: res.message
            }})
        }
    }, [actions]);
    const acknowledge = useCallback(async function (chatId: string, messages: Read[]) {
        let res;
        if (!state.user) {
            console.error("No user set up");
            return;
        }
        res = await readMessages(chatId, messages);
        if (res.type === "success") {
            actions.setLoadingState(LoadingState.Success);
            actions.acknowledge({
                chatId,
                ids: messages.map((m) => m.messageID),
                readerId: state.user.userID
            })
        }
    }, [state.user, actions]);
    const handleSendMessage = useCallback(async function () {
        let res;
        const {currentChatId, inputMessage, user} = state;
        if (!inputMessage.trim() || !currentChatId || !user) {
            return;
        }
        actions.setLoadingState(LoadingState.Pending);
        res = await sendMessage(currentChatId, inputMessage);
        if (res.type === "success") {
            actions.setLoadingState(LoadingState.Success);
            actions.addMessages({chatID: currentChatId, values:[res.value]});
            actions.setInputMessage('');
        } else {
            actions.setLoadingState(LoadingState.Error);
            dispatch({type: "set_error", payload: {
                title: "Error sending message",
                desc: res.message
            }})
        }
    }, [state.inputMessage, state.currentChatId, state.user, actions]);
    const currentChat = useMemo(function () {
        const {currentChatId = ""} = state;
        const item = state.chats.filter((c) => c.id === currentChatId)[0];
        const groupedMessages: ChatMessages = {groups: []};
        let tmp;
        let unread: Message[];
        if (!item) {
            return;
        }
        unread = item.messages.filter(
            (m) => !m.readers.includes(state.user?.userID ?? "")
        );
        tmp = item.messages.reduce(
            groupReducer(state.user?.userID ?? ""),
            {messages: {}}
        );
        groupedMessages.lastUnread = tmp.lastUnreadId;
        groupedMessages.groups = Object.entries(tmp.messages).sort(
            (e1, e2) => e1[1][0].timestamp - e2[1][0].timestamp
        ).map((e) => Object.assign({}, {label: e[0], items: e[1]}));
        return {groupedMessages, item, unread};
    }, [state]);
    const unreadChats = useMemo(function () {
        return state.chats.reduce(function (
            acc: Record<string, number>,
            val: Chat
        ): Record<string, number> {
            acc[val.id] = val.messages.filter(
                (m) => !m.readers.includes(state.user?.email ?? "")
            ).length;
            return acc;
        }, Object.create(null));
    }, [state]);

    return {
        acknowledge,
        actions,
        currentChat,
        fetchChats: getChat,
        fetchMessages,
        handleSendMessage,
        state,
        unreadChats
    };
}

export function ChatProvider({children}: {children: React.ReactNode}) {
    const value = useChatValue();
    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
export function useChat() {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}

